import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { telasApi } from '../api/telas'
import { MenuItemResponse, TelaResponse } from '../types/api'
import TelaTreeView from '../components/Telas/TelaTreeView'
import TelaOrdenacaoList from '../components/Telas/TelaOrdenacaoList'
import { useAuth } from '../contexts/AuthContext'
import RefreshButton from '../components/RefreshButton'
import ActionBar from '../components/ActionBar'
import { createLogger } from '../utils/logger'

const logger = createLogger('TelasPage')

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const TelasPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [tabValue, setTabValue] = useState(0)
  const [tipoSistema, setTipoSistema] = useState<'ADMIN' | 'GALATEA_WEB' | 'all'>('all')

  // Buscar lista de todas as telas (para ordenação)
  const { data: todasTelas = [], isLoading: isLoadingTelas } = useQuery<TelaResponse[]>({
    queryKey: ['telas', 'todas'],
    queryFn: () => telasApi.list({ incluir_inativos: false }),
  })

  // Buscar árvore de telas (usando o próprio usuário atual, que deve ser Super Admin)
  const { data: arvoreData, isLoading: isLoadingArvore, error, refetch } = useQuery({
    queryKey: ['telas', 'arvore', currentUser?.id, tipoSistema],
    queryFn: () => {
      // Garantir que currentUser.id é um número
      const userId = typeof currentUser!.id === 'string' ? parseInt(currentUser!.id, 10) : Number(currentUser!.id)
      if (isNaN(userId)) {
        throw new Error(`ID do usuário inválido: ${currentUser!.id}`)
      }
      logger.debug('Buscando árvore de telas para userId:', userId, 'tipo:', typeof userId)
      return telasApi.getArvoreUsuario(userId, {
        tipo_sistema: tipoSistema === 'all' ? undefined : tipoSistema,
      })
    },
    enabled: !!currentUser?.id,
  })

  // Mutation para reordenar telas (apenas globais)
  const reordenarMutation = useMutation({
    mutationFn: async (telas: Array<{ id: number; posicao_menu: number }>) => {
      logger.debug('Reordenando telas globais:', telas)
      const resultado = await telasApi.reordenar(telas, true) // apenasGlobais = true
      logger.debug('Telas reordenadas:', resultado)
      return resultado
    },
    onSuccess: () => {
      logger.debug('Mutation sucesso, invalidando queries')
      // IMPORTANTE: Invalidar queries SEM refetch automático para evitar requisições incorretas
      // Usar exact: true e refetchType: 'none' para invalidar sem refetch automático
      queryClient.invalidateQueries({ 
        queryKey: ['telas', 'todas'],
        exact: true,
        refetchType: 'none' // Não fazer refetch automático
      })
      queryClient.invalidateQueries({ 
        queryKey: ['menu'],
        exact: false,
        refetchType: 'none' // Não fazer refetch automático
      })
      // Recarregar APENAS a lista de telas manualmente (query específica)
      queryClient.refetchQueries({ 
        queryKey: ['telas', 'todas'],
        exact: true,
        type: 'active' // Apenas queries ativas
      })
      // Refetch da árvore apenas se necessário e apenas se a tab estiver ativa
      if (tabValue === 0) {
        // Usar setTimeout para evitar conflitos com outras queries
        setTimeout(() => {
          refetch()
        }, 100)
      }
    },
    onError: (error) => {
      logger.error('Erro na mutation:', error)
    },
  })

  const handleToggleNovaAba = useCallback(
    async (telaId: number, abrirNovaAba: boolean) => {
      await telasApi.update(telaId, { abrir_nova_aba: abrirNovaAba })

      queryClient.invalidateQueries({
        queryKey: ['telas', 'todas'],
        exact: true,
        refetchType: 'none',
      })
      queryClient.invalidateQueries({
        queryKey: ['menu'],
        exact: false,
        refetchType: 'none',
      })

      queryClient.refetchQueries({
        queryKey: ['telas', 'todas'],
        exact: true,
        type: 'active',
      })
      queryClient.refetchQueries({
        queryKey: ['menu'],
        exact: false,
        type: 'active',
      })

      if (tabValue === 0) {
        refetch()
      }
    },
    [queryClient, refetch, tabValue]
  )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleTelaClick = (tela: MenuItemResponse) => {
    if (!tela.rota) return

    const rotaNormalizada =
      tela.rota.startsWith('http://') || tela.rota.startsWith('https://') || tela.rota.startsWith('/')
        ? tela.rota
        : `/${tela.rota}`

    if (tela.abrir_nova_aba) {
      window.open(rotaNormalizada, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = rotaNormalizada
    }
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <ActionBar>
        <RefreshButton onClick={() => refetch()} disabled={isLoadingArvore} title="Atualizar" />
      </ActionBar>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" component="div">
          <strong>Acesso Restrito: Super Administrador</strong>
          <br />
          Esta página permite gerenciar a configuração padrão global das telas.
          <br />
          A ordem definida aqui se aplica a TODAS as empresas, áreas funcionais e níveis de acesso.
          <br />
          <strong>Nota:</strong> Esta é a configuração padrão global. Não é necessário selecionar usuário específico.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Tipo Sistema</InputLabel>
            <Select
              value={tipoSistema}
              label="Tipo Sistema"
              onChange={(e) => setTipoSistema(e.target.value as 'ADMIN' | 'GALATEA_WEB' | 'all')}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="ADMIN">ADMIN</MenuItem>
              <MenuItem value="GALATEA_WEB">GALATEA_WEB</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Visualização em Árvore" />
          <Tab label="Ordenação de Telas" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {isLoadingArvore ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Erro ao carregar árvore de telas: {error instanceof Error ? error.message : 'Erro desconhecido'}
            </Alert>
          ) : arvoreData?.telas ? (
            <TelaTreeView telas={arvoreData.telas} onTelaClick={handleTelaClick} />
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>
              Nenhuma tela encontrada para este usuário
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isLoadingTelas ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : todasTelas.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Nenhuma tela encontrada
            </Alert>
          ) : (
            <TelaOrdenacaoList
              telas={todasTelas}
              tipoSistema={tipoSistema}
              apenasGlobais={true}
              titulo="Ordenação de Telas - Configuração Padrão Global"
              onReordenar={async (telas) => {
                await reordenarMutation.mutateAsync(telas)
              }}
              onToggleNovaAba={handleToggleNovaAba}
            />
          )}
        </TabPanel>
      </Paper>
    </Box>
  )
}

export default TelasPage

